import React, { useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Close } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { formStyles } from '../../../util/styles';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { FormControl, FormLabel } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import CreatableGroupSelect from '../CreatableGroupSelect';
import axios from 'axios';
import Checkbox from '@mui/material/Checkbox';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 10,
  },
  checkbox: {
    marginTop: -9,
    alignSelf: 'flex-start',
  },
  subtitle: {
    display: 'block',
    color: 'gray',
    fontSize: '0.775rem',
  },
}));

const EditUserModal = ({
  open,
  onClose,
  userData,
  isAdmin,
  selfUserEmail,
}) => {
  if (!userData) {
    return null;
  }

  const checkboxClasses = useStyles();
  const classes = formStyles();
  const [userInfo, setUserInfo] = useState(userData);
  const [moveCases, setMoveCases] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleEdit = async () => {
    let errorList = [];

    if (!userInfo.name) {
      errorList.push('Name must be populated');
    }

    if (!userInfo.groupName) {
      errorList.push('User must be part of a group');
    }

    if (!userInfo.companyName) {
      errorList.push('Org name must be populated');
    }

    if (errorList.length > 0) {
      setErrors(errorList);
    } else {
      try {
        const editUserBody = {
          ...userInfo,
          groups: [userInfo.groupID],
          moveCases,
        };
        setIsLoading(true);
        const res = await axios.put('/api/user', editUserBody);
        setIsLoading(false);
        onClose(true);
      } catch (error) {
        setIsLoading(false);
        setErrors([error]);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      className={classes.form}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ pr: 6 }}>
        Edit User
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
          size="large">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Collapse in={errors && errors.length > 0}>
          <Alert severity="error" className={classes.alert}>
            Edit Failed:
            <ul>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        </Collapse>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="name"
              name="name"
              label="Name"
              autoFocus
              value={userInfo.name}
              onChange={(event) =>
                setUserInfo({
                  ...userInfo,
                  name: event.target.value,
                })
              }
            />
          </Grid>
          <Grid size={12}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="email"
              name="email"
              label="Email"
              value={userInfo.email}
              disabled={true}
            />
          </Grid>
          {isAdmin && selfUserEmail === userData.email && (<Grid size={12}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="company"
              name="company"
              label="Company"
              value={userInfo.companyName}
              onChange={(event) =>
                setUserInfo({
                  ...userInfo,
                  companyName: event.target.value,
                })
              }
            />
          </Grid>)}
          <Grid size={12}>
            <FormLabel component={'legend'}>Group</FormLabel>
            <CreatableGroupSelect
              create={true}
              value={{
                label: userInfo.groupName,
                value: userInfo.groupID,
              }}
              onChange={(group) => {
                setUserInfo({
                  ...userInfo,
                  groupName: group.label,
                  groupID: group.value,
                });
              }}
              disabled={!isAdmin}
            />
            {userInfo.groupID !== userData.groupID && (
              <Grid size={12} style={{ marginTop: 10 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      classes={{ root: checkboxClasses.checkbox }}
                    />
                  }
                  label={
                    <>
                      Move cases to new group
                      <Typography
                        component="span"
                        classes={{
                          root: checkboxClasses.subtitle,
                        }}
                      >
                        This will be done automatically if there are
                        no other users in the group
                      </Typography>
                    </>
                  }
                  onChange={() => setMoveCases(!moveCases)}
                />
              </Grid>
            )}
          </Grid>
          {isAdmin && selfUserEmail !== userData.email && (
            <Grid size={12}>
              <FormControl component={'fieldset'}>
                <FormLabel component={'legend'}>User Role</FormLabel>
                <RadioGroup
                  value={userInfo.role ? userInfo.role : 'User'}
                  onChange={(e) => {
                    setUserInfo({
                      ...userInfo,
                      role: e.target.value,
                    });
                  }}
                >
                  <Grid container>
                    <Grid>
                      <FormControlLabel
                        control={<Radio />}
                        value={'User'}
                        label={'User'}
                      />
                    </Grid>
                    <Grid>
                      <FormControlLabel
                        control={<Radio />}
                        value={'Admin'}
                        label={'Admin'}
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={handleEdit}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={isLoading}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
